import type { TFunction } from 'i18next';
import { ApiError } from './api.js';
import { estElevationNonAcceptee } from './elevation.js';

/**
 * Le message d'une erreur de l'API, pour la ligne concernée (ARTO-58).
 *
 * Les motifs de `409 conflit.version` (`details.raison`) et de `400
 * validation.invalide` sont ceux de `docs/dev/api.md` (§ 12.2, § 12.10) : on
 * les traduit, fr et en. Un motif inconnu garde le message de l'API, qui est
 * déjà une phrase française — jamais un code brut.
 *
 * `ressource` choisit le texte d'un doublon : seuls les coûts ont un index
 * unique (un coût par poste et par mois).
 */
export function messageErreurSaisie(t: TFunction, erreur: unknown, ressource?: string): string {
  const dire = (cle: string, options?: Record<string, unknown>): string =>
    String(t(`saisies.erreurs.${cle}`, options));

  if (estElevationNonAcceptee(erreur)) return dire('elevationRefusee');
  if (!(erreur instanceof ApiError)) return dire('reseau');

  const details = (erreur.details ?? {}) as {
    raison?: string;
    champs?: unknown;
    mois?: unknown;
    somme?: unknown;
  };
  if (erreur.status === 409 || erreur.code === 'conflit.version') {
    switch (details.raison) {
      case 'doublon':
        return String(
          t(`saisies.erreurs.doublon.${ressource ?? 'defaut'}`, {
            defaultValue: dire('doublon.defaut'),
          }),
        );
      case 'creance_non_soldee':
        return dire('creanceNonSoldee');
      case 'parts_depassees':
        return dire('partsDepassees');
      case 'versements_existants':
        return dire('versementsExistants');
      case 'versement_existant':
        return dire('versementExistant');
      case 'role_plateforme_existant':
        return dire('roleExistant');
      case 'compte_en_suppression':
        return dire('compteEnSuppression');
      case 'invitation_concurrente':
        return dire('invitationConcurrente');
      case 'mois_cloture': {
        // Un ou plusieurs mois clôturés : l'API les nomme en `AAAA-MM`.
        // Un abonnement ou un versement a son propre texte (`context`) : ce
        // qui reste permis n'est pas le même. Sans texte propre, le commun.
        const mois = Array.isArray(details.mois) ? details.mois.map(String) : [];
        return mois.length > 0
          ? dire('moisCloture', {
              count: mois.length,
              mois: mois.join(', '),
              ...(ressource === 'abonnements' || ressource === 'versements'
                ? { context: ressource }
                : {}),
            })
          : erreur.message;
      }
      case 'version_existante':
        return dire('versionExistante');
      case 'cloture_impossible':
        return dire('clotureImpossible');
      case 'mois_non_termine':
        return dire('moisNonTermine');
      case 'mois_deja_cloture':
        return dire('moisDejaCloture');
      case 'reste_deja_designe':
        return dire('resteDejaDesigne');
      case 'exclusion_existante':
        return dire('exclusionExistante');
      default:
        return erreur.message;
    }
  }
  if (erreur.status === 400 || erreur.code === 'validation.invalide') {
    if (details.raison === 'compte_non_associe') return dire('compteNonAssocie');
    const champs = Array.isArray(details.champs)
      ? (details.champs as Array<{ chemin?: string }>)
          .map((champ) => champ.chemin)
          .filter((chemin): chemin is string => typeof chemin === 'string' && chemin !== '')
      : [];
    return champs.length > 0
      ? dire('invalide', { champs: champs.join(', ') })
      : dire('invalideSansChamp');
  }
  if (erreur.status === 422 && erreur.code === 'comptabilite.repartition_invalide') {
    if (details.raison === 'somme_parts' && typeof details.somme === 'number') {
      return dire('repartitionSomme', { somme: details.somme / 100 });
    }
    if (details.raison === 'beneficiaire_en_double') return dire('repartitionDouble');
    if (details.raison === 'beneficiaire_inconnu') return dire('repartitionInconnu');
    if (details.raison === 'reste_avec_exclusions') return dire('resteAvecExclusions');
    if (details.raison === 'exclusion_du_reste') return dire('exclusionDuReste');
    if (details.raison === 'galerie_de_test') return dire('exclusionGalerieTest');
  }
  if (erreur.status === 403) return dire('droits');
  if (erreur.status === 404) return dire('introuvable');
  return erreur.message || dire('inconnue');
}
